import type { GlobalSearchResponse } from '@/api/search.service';

export const ENTITY_LINK_TYPES = ['booking', 'invoice', 'customer', 'car'] as const;
export type EntityLinkType = (typeof ENTITY_LINK_TYPES)[number];

export const ENTITY_LINK_GROUPS = ['bookings', 'invoices', 'customers', 'cars'] as const;
export type EntityLinkGroup = (typeof ENTITY_LINK_GROUPS)[number];

export type EntityLinkItem = {
  entityType: EntityLinkType;
  group: EntityLinkGroup;
  id: string;
  label: string;
  secondary: string;
};

const ENTITY_LINK_PATHS: Record<EntityLinkType, (id: string) => string> = {
  booking: (id) => `/booking-list/${id}`,
  invoice: (id) => `/invoice/${id}`,
  customer: (id) => `/user/${id}`,
  car: (id) => `/view-car/${id}`,
};

export function entityLinkHref(entityType: unknown, id: unknown): string | null {
  if (typeof entityType !== 'string' || typeof id !== 'string') return null;
  if (!(entityType in ENTITY_LINK_PATHS)) return null;
  return ENTITY_LINK_PATHS[entityType as EntityLinkType](id);
}

const joinSecondary = (...parts: Array<string | number | null | undefined>) =>
  parts
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(' · ');

export function flattenEntitySearchResults(data: GlobalSearchResponse): EntityLinkItem[] {
  return [
    ...data.bookings.map((booking) => ({
      entityType: 'booking' as const,
      group: 'bookings' as const,
      id: booking.id,
      label: String(booking.bookingNumber),
      secondary: joinSecondary(booking.customerName || 'n/a', booking.licensePlate),
    })),
    ...data.invoices.map((invoice) => ({
      entityType: 'invoice' as const,
      group: 'invoices' as const,
      id: invoice.id,
      label: invoice.completeInvoiceNumber,
      secondary: joinSecondary(
        invoice.customerName || 'n/a',
        invoice.bookingNumber != null ? `#${invoice.bookingNumber}` : undefined,
      ),
    })),
    ...data.customers.map((customer) => ({
      entityType: 'customer' as const,
      group: 'customers' as const,
      id: customer.id,
      label: `${customer.firstName} ${customer.lastName}`.trim(),
      secondary: joinSecondary(customer.email, customer.company),
    })),
    ...data.cars.map((car) => {
      const fleetNumber = (car as GlobalSearchResponse['cars'][number] & { fleetNumber?: number })
        .fleetNumber;
      return {
        entityType: 'car' as const,
        group: 'cars' as const,
        id: car.id,
        label: car.licensePlate?.trim() || String(fleetNumber ?? ''),
        secondary: joinSecondary(`${car.makeName} ${car.model}`.trim()),
      };
    }),
  ];
}
